import React, { useState, useEffect } from "react";
import { useCategoriesService } from "../hooks/useCategoriesService";
import {
  CategoryWithTemplates,
  TodoTemplate,
  TodoTemplateWithItems,
  CreateTodoTemplateData,
  UpdateTodoTemplateData,
  TemplateItem,
  CreateTemplateItemData,
  UpdateTemplateItemData,
  PRIORITY_COLORS,
  CATEGORY_COLORS,
} from "../types/todo";

export const TemplatesManager: React.FC = () => {
  const {
    loading,
    error,
    getCategories,
    getTemplatesByCategory,
    getTemplateById,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplateItems,
    createTemplateItem,
    updateTemplateItem,
    deleteTemplateItem,
    reorderTemplateItems,
    duplicateTemplate,
    searchTemplates,
    clearError,
  } = useCategoriesService();

  const [categories, setCategories] = useState<CategoryWithTemplates[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [templates, setTemplates] = useState<TodoTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<TodoTemplateWithItems | null>(null);
  const [templateItems, setTemplateItems] = useState<TemplateItem[]>([]);

  // Form states
  const [showCreateTemplateForm, setShowCreateTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TodoTemplate | null>(
    null
  );
  const [showCreateItemForm, setShowCreateItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState<TemplateItem | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<TodoTemplateWithItems[]>(
    []
  );

  const [templateFormData, setTemplateFormData] = useState({
    name: "",
    description: "",
    category_id: "",
    is_public: false,
  });

  const [itemFormData, setItemFormData] = useState({
    title: "",
    description: "",
    priority: "medium" as "low" | "medium" | "high",
  });

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      loadTemplates(selectedCategory);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedTemplate) {
      loadTemplateItems(selectedTemplate.id);
    }
  }, [selectedTemplate]);

  const loadCategories = async () => {
    const data = await getCategories();
    setCategories(data);
    if (data.length > 0 && !selectedCategory) {
      setSelectedCategory(data[0].id);
    }
  };

  const loadTemplates = async (categoryId: string) => {
    const data = await getTemplatesByCategory(categoryId);
    setTemplates(data);
    setSelectedTemplate(null);
    setTemplateItems([]);
  };

  const loadTemplateItems = async (templateId: string) => {
    const data = await getTemplateItems(templateId);
    setTemplateItems(data);
  };

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCategory) return;

    const result = await createTemplate({
      ...templateFormData,
      category_id: selectedCategory,
    });

    if (result) {
      await loadTemplates(selectedCategory);
      setShowCreateTemplateForm(false);
      resetTemplateForm();
    }
  };

  const handleUpdateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTemplate) return;

    const result = await updateTemplate(editingTemplate.id, templateFormData);
    if (result) {
      await loadTemplates(selectedCategory!);
      if (selectedTemplate?.id === editingTemplate.id) {
        const updatedTemplate = await getTemplateById(editingTemplate.id);
        setSelectedTemplate(updatedTemplate);
      }
      setEditingTemplate(null);
      resetTemplateForm();
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this template? This action cannot be undone."
      )
    ) {
      const success = await deleteTemplate(templateId);
      if (success) {
        await loadTemplates(selectedCategory!);
        if (selectedTemplate?.id === templateId) {
          setSelectedTemplate(null);
          setTemplateItems([]);
        }
      }
    }
  };

  const handleCreateItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplate) return;

    const result = await createTemplateItem({
      ...itemFormData,
      template_id: selectedTemplate.id,
      order_index: templateItems.length,
    });

    if (result) {
      await loadTemplateItems(selectedTemplate.id);
      setShowCreateItemForm(false);
      resetItemForm();
    }
  };

  const handleUpdateItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;

    const result = await updateTemplateItem(editingItem.id, itemFormData);
    if (result) {
      await loadTemplateItems(selectedTemplate!.id);
      setEditingItem(null);
      resetItemForm();
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      const success = await deleteTemplateItem(itemId);
      if (success) {
        await loadTemplateItems(selectedTemplate!.id);
      }
    }
  };

  const handleDuplicateTemplate = async (templateId: string) => {
    const result = await duplicateTemplate(templateId);
    if (result) {
      await loadTemplates(selectedCategory!);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.trim()) {
      const results = await searchTemplates(searchQuery);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const resetTemplateForm = () => {
    setTemplateFormData({
      name: "",
      description: "",
      category_id: selectedCategory || "",
      is_public: false,
    });
  };

  const resetItemForm = () => {
    setItemFormData({
      title: "",
      description: "",
      priority: "medium",
    });
  };

  const startEditTemplate = (template: TodoTemplate) => {
    setEditingTemplate(template);
    setTemplateFormData({
      name: template.name,
      description: template.description || "",
      category_id: template.category_id,
      is_public: template.is_public,
    });
  };

  const startEditItem = (item: TemplateItem) => {
    setEditingItem(item);
    setItemFormData({
      title: item.title,
      description: item.description || "",
      priority: item.priority,
    });
  };

  const selectTemplate = async (template: TodoTemplate) => {
    const fullTemplate = await getTemplateById(template.id);
    setSelectedTemplate(fullTemplate);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="m-0 text-blue-500 text-2xl font-semibold">
          Zarządzanie Harmonogramami
        </h2>

        {/* Search */}
        <div className="flex gap-3 items-center">
          <input
            type="text"
            placeholder="Wyszukaj harmonogramy..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            className="px-3 py-2 border border-gray-300 rounded w-48 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-500 text-white border-none rounded cursor-pointer hover:bg-blue-600 transition-colors"
          >
            Szukaj
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-5 text-red-500 flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={clearError}
            className="bg-none border-none text-red-500 cursor-pointer text-base hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="bg-white border border-gray-300 rounded-lg p-5 mb-6">
          <h3 className="m-0 mb-4 text-lg font-semibold">
            Search Results ({searchResults.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {searchResults.map((template) => (
              <div
                key={template.id}
                className="p-3 border border-gray-200 rounded cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setSelectedCategory(template.category_id);
                  selectTemplate(template);
                  setSearchResults([]);
                  setSearchQuery("");
                }}
              >
                <h4 className="m-0 mb-1 text-sm font-medium text-gray-800">
                  {template.name}
                </h4>
                <p className="m-0 mb-1 text-xs text-gray-600">
                  {template.category?.name} • {template.items_count} items
                </p>
                {template.description && (
                  <p className="m-0 text-xs text-gray-500">
                    {template.description.substring(0, 60)}...
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_400px] gap-6 h-[calc(100vh-200px)]">
        {/* Categories Sidebar */}
        <div className="bg-white border border-gray-300 rounded-lg p-5 h-fit">
          <h3 className="m-0 mb-4 text-gray-800 text-lg font-semibold">
            Kategorie
          </h3>
          <div className="flex flex-col gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`p-3 rounded-md cursor-pointer text-left flex items-center gap-2 transition-colors ${
                  selectedCategory === category.id
                    ? "bg-blue-50 border-blue-500 border"
                    : "bg-transparent border-gray-300 border hover:bg-gray-50"
                }`}
              >
                <span
                  className="w-6 h-6 rounded flex items-center justify-center text-xs"
                  style={{
                    backgroundColor: category.color || CATEGORY_COLORS[0],
                  }}
                >
                  {category.icon || "📝"}
                </span>
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-800">
                    {category.name}
                  </div>
                  <div className="text-xs text-gray-600">
                    {category.templates_count || 0} harmonogramów
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Templates List */}
        <div className="bg-white border border-gray-300 rounded-lg p-5 h-[calc(100vh-300px)] overflow-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="m-0 text-gray-800 text-lg font-semibold">
              Harmonogramy
            </h3>
            <button
              onClick={() => setShowCreateTemplateForm(true)}
              disabled={!selectedCategory}
              className={`px-3 py-1.5 text-white border-none rounded text-sm ${
                selectedCategory
                  ? "bg-blue-600 cursor-pointer hover:bg-blue-700"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              + Nowy Harmonogram
            </button>
          </div>

          {/* Create/Edit Template Form */}
          {(showCreateTemplateForm || editingTemplate) && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-4">
              <h4 className="m-0 mb-3 text-base font-medium">
                {editingTemplate ? "Edytuj harmonogram" : "Utwórz harmonogram"}
              </h4>
              <form
                onSubmit={
                  editingTemplate ? handleUpdateTemplate : handleCreateTemplate
                }
              >
                <div className="mb-3">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Nazwa *
                  </label>
                  <input
                    type="text"
                    value={templateFormData.name}
                    onChange={(e) =>
                      setTemplateFormData({
                        ...templateFormData,
                        name: e.target.value,
                      })
                    }
                    required
                    className="w-full px-2 py-1.5 border border-gray-300 rounded box-border focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="mb-3">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Opis
                  </label>
                  <textarea
                    value={templateFormData.description}
                    onChange={(e) =>
                      setTemplateFormData({
                        ...templateFormData,
                        description: e.target.value,
                      })
                    }
                    rows={2}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded box-border resize-y focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="mb-3">
                  <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={templateFormData.is_public}
                      onChange={(e) =>
                        setTemplateFormData({
                          ...templateFormData,
                          is_public: e.target.checked,
                        })
                      }
                      className="rounded"
                    />
                    Publiczny harmonogram
                  </label>
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`px-3 py-1.5 text-white border-none rounded text-sm ${
                      loading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 cursor-pointer hover:bg-blue-700"
                    }`}
                  >
                    {loading
                      ? "Zapisywanie..."
                      : editingTemplate
                      ? "Edytuj"
                      : "Utwórz"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateTemplateForm(false);
                      setEditingTemplate(null);
                      resetTemplateForm();
                    }}
                    className="px-3 py-1.5 bg-transparent text-gray-600 border border-gray-300 rounded cursor-pointer text-sm hover:bg-gray-50"
                  >
                    Anuluj
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Templates List */}
          {templates.length === 0 ? (
            <div className="text-center py-10 text-gray-600">
              {selectedCategory
                ? "Brak harmonogramów w tej kategorii."
                : "Wybierz kategorię, aby zobaczyć harmonogramy."}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className={`p-4 rounded-md cursor-pointer transition-colors ${
                    selectedTemplate?.id === template.id
                      ? "border-2 border-blue-600 bg-blue-50"
                      : "border border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                  onClick={() => selectTemplate(template)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="m-0 mb-1 text-base font-medium">
                        {template.name}
                      </h4>
                      {template.description && (
                        <p className="m-0 mb-2 text-sm text-gray-600">
                          {template.description}
                        </p>
                      )}
                      <div className="text-xs text-gray-500">
                        Utworzony{" "}
                        {new Date(template.created_at).toLocaleDateString()}
                        {template.is_public && (
                          <span className="ml-2 px-1.5 py-0.5 bg-gray-100 rounded text-xs">
                            Publiczny
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditTemplate(template);
                        }}
                        className="px-2 py-1 bg-transparent text-blue-500 border border-blue-500 rounded text-xs cursor-pointer hover:bg-blue-50 transition-colors"
                      >
                        Edytuj
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicateTemplate(template.id);
                        }}
                        className="px-2 py-1 bg-transparent text-green-600 border border-green-600 rounded text-xs cursor-pointer hover:bg-green-50 transition-colors"
                      >
                        Utwórz kopię
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTemplate(template.id);
                        }}
                        className="px-2 py-1 bg-transparent text-red-500 border border-red-500 rounded text-xs cursor-pointer hover:bg-red-50 transition-colors"
                      >
                        Usuń
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Template Items */}
        <div className="bg-white border border-gray-300 rounded-lg p-5 h-[calc(100vh-300px)] overflow-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="m-0 text-gray-800">
              Elementy harmonogramu
              {selectedTemplate && (
                <span className="text-sm font-normal text-gray-600">
                  {" "}
                  • {selectedTemplate.name}
                </span>
              )}
            </h3>
            <button
              onClick={() => setShowCreateItemForm(true)}
              disabled={!selectedTemplate}
              className={`px-3 py-1.5 text-white border-none rounded text-sm ${
                selectedTemplate
                  ? "bg-green-600 cursor-pointer hover:bg-green-700"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              + Nowy element
            </button>
          </div>

          {/* Create/Edit Item Form */}
          {(showCreateItemForm || editingItem) && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-4">
              <h4 className="m-0 mb-3">
                {editingItem ? "Edytuj element" : "Utwórz element"}
              </h4>
              <form
                onSubmit={editingItem ? handleUpdateItem : handleCreateItem}
              >
                <div className="mb-3">
                  <label className="block mb-1 text-sm font-medium">
                    Tytuł *
                  </label>
                  <input
                    type="text"
                    value={itemFormData.title}
                    onChange={(e) =>
                      setItemFormData({
                        ...itemFormData,
                        title: e.target.value,
                      })
                    }
                    required
                    className="w-full px-2 py-1.5 border border-gray-300 rounded box-border focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="mb-3">
                  <label className="block mb-1 text-sm font-medium">Opis</label>
                  <textarea
                    value={itemFormData.description}
                    onChange={(e) =>
                      setItemFormData({
                        ...itemFormData,
                        description: e.target.value,
                      })
                    }
                    rows={2}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded box-border resize-y focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="mb-3">
                  <label className="block mb-1 text-sm font-medium">
                    Priorytet
                  </label>
                  <select
                    value={itemFormData.priority}
                    onChange={(e) =>
                      setItemFormData({
                        ...itemFormData,
                        priority: e.target.value as "low" | "medium" | "high",
                      })
                    }
                    className="w-full px-2 py-1.5 border border-gray-300 rounded box-border focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">Niski</option>
                    <option value="medium">Średni</option>
                    <option value="high">Wysoki</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`px-3 py-1.5 bg-green-500 text-white border-none rounded text-sm transition-colors ${
                      loading
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer hover:bg-green-600"
                    }`}
                  >
                    {loading
                      ? "Zapisywanie..."
                      : editingItem
                      ? "Edytuj"
                      : "Utwórz"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateItemForm(false);
                      setEditingItem(null);
                      resetItemForm();
                    }}
                    className="px-3 py-1.5 bg-transparent text-gray-600 border border-gray-300 rounded text-sm cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    Anuluj
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Template Items List */}
          {!selectedTemplate ? (
            <div className="text-center py-10 text-gray-600">
              Wybierz harmonogram, aby zobaczyć jego elementy.
            </div>
          ) : templateItems.length === 0 ? (
            <div className="text-center py-10 text-gray-600">
              Ten harmonogram nie ma jeszcze żadnych elementów.
              <br />
              Kliknij "Nowy element", aby dodać pierwszy element.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {templateItems.map((item, index) => (
                <div
                  key={item.id}
                  className="p-3 border border-gray-200 rounded bg-white"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-gray-500">
                          #{index + 1}
                        </span>
                        <span
                          className="px-1.5 py-0.5 text-white rounded text-xs font-medium"
                          style={{
                            backgroundColor: PRIORITY_COLORS[item.priority],
                          }}
                        >
                          {item.priority.toUpperCase()}
                        </span>
                      </div>
                      <h5 className="m-0 mb-1 text-sm font-medium">
                        {item.title}
                      </h5>
                      {item.description && (
                        <p className="m-0 text-xs text-gray-600">
                          {item.description}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => startEditItem(item)}
                        className="px-1.5 py-0.5 bg-transparent text-blue-500 border border-blue-500 rounded text-xs cursor-pointer hover:bg-blue-50 transition-colors"
                      >
                        Edytuj
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="px-1.5 py-0.5 bg-transparent text-red-500 border border-red-500 rounded text-xs cursor-pointer hover:bg-red-50 transition-colors"
                      >
                        Usuń
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
